import type { HseReportType } from './constants.js';

/**
 * Sağlık verisi (ADR 0062 §1): özel nitelikli kişisel veri [GDPR md. 9; KVKK md. 6]; gemiadamının
 * tıbbi bilgisi gizlidir [MLC 2006 Standart A4.1 ¶2]. Bu alanları yalnız `hse_medical.view`
 * yetkilisi görür ve yazar. Yaralı sayısı (`injuredCount`) gizlenmez: istatistik girdisidir,
 * kişinin sağlık bilgisi değildir. Tablo türe göredir; yeni sağlık alanı buraya eklenir.
 */
export const HSE_MEDICAL_FIELDS: Readonly<Record<HseReportType, readonly string[]>> = {
  non_conformity: [],
  deficiency: [],
  near_miss: [],
  accident: ['injuries', 'medicalMeasures'],
};

export function hseMedicalFields(type: HseReportType): readonly string[] {
  return HSE_MEDICAL_FIELDS[type] ?? [];
}

type Details = Readonly<Record<string, unknown>> | null | undefined;

const fieldValue = (details: Details, key: string): unknown => {
  const value = details?.[key];
  return value === undefined || value === '' ? null : value;
};

/**
 * Yetkisiz kişiye giden ayrıntı: sağlık alanları `null`, gizlenen alanların listesi döner. Boş
 * alan da gizli bildirilir: "kayıtlı mı" bilgisi sızmaz.
 */
export function redactHseMedical(
  type: HseReportType,
  details: Details,
): { details: Record<string, unknown>; hidden: string[] } {
  const fields = hseMedicalFields(type);
  const out: Record<string, unknown> = { ...(details ?? {}) };
  for (const key of fields) out[key] = null;
  return { details: out, hidden: [...fields] };
}

/**
 * Yetkisiz kişinin düzenlemesi: sağlık alanları saklı değerden alınır (süzülmüş istemci veriyi
 * silemez); yeni kayıtta (`stored` yok) boş kalır.
 */
export function mergeHseMedical(
  type: HseReportType,
  stored: Details,
  incoming: Details,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(incoming ?? {}) };
  for (const key of hseMedicalFields(type)) out[key] = fieldValue(stored, key);
  return out;
}

/** Sağlık alanlarından biri değişti mi (boş ve eksik aynı sayılır). */
export function hseMedicalChanged(type: HseReportType, before: Details, after: Details): boolean {
  return hseMedicalFields(type).some(
    (key) => JSON.stringify(fieldValue(before, key)) !== JSON.stringify(fieldValue(after, key)),
  );
}
